import { authQueryOptions } from "@/services/auth.service";
import { hashKey, QueryClient } from "@tanstack/react-query";
import ky, { HTTPError } from "ky";
import lassoUrls from "./lasso-urls";
import { storage } from "./storage";

export const baseUrl = lassoUrls.getApiUrl();

export interface ApiException {
  message: string;
  httpStatusCode: number;
}

export interface LassoException extends ApiException {
  errorCode: number;
  errorMessage?: string;
}

export type PagedResult<T> = {
  page: number;
  pageSize: number;
  results: T[];
  resultsFound: number;
  resultsReturned: number;
  totalPages: number;
};

export const isGeneralException = (error: unknown): error is ApiException => {
  return (
    (error as ApiException).httpStatusCode !== undefined &&
    (error as LassoException).errorCode === undefined
  );
};
export const isLassoException = (error: unknown): error is LassoException => {
  return (error as LassoException).errorCode !== undefined;
};

export function buildQueryString<K extends string>(
  params: Partial<Record<K, unknown>>,
) {
  const urlSearchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    urlSearchParams.set(
      key,
      Array.isArray(value) ? value.join(",") : value.toString(),
    );
  }
  return urlSearchParams.size ? `?${urlSearchParams}` : "";
}

// The API allows 500 requests per minute. Keep a margin below that and delay
// requests (including ky retries, which also pass through beforeRequest) once
// the budget for the current window is spent. This is per tab.
const RATE_LIMIT_REQUESTS = 450;
const RATE_LIMIT_WINDOW_MS = 60_000;
const requestTimestamps: number[] = [];

async function waitForRateLimit(signal: AbortSignal) {
  for (;;) {
    // A request aborted while queued must not take a slot.
    signal.throwIfAborted();

    const now = Date.now();
    while (
      requestTimestamps.length &&
      requestTimestamps[0] <= now - RATE_LIMIT_WINDOW_MS
    ) {
      requestTimestamps.shift();
    }
    if (requestTimestamps.length < RATE_LIMIT_REQUESTS) {
      requestTimestamps.push(now);
      return;
    }
    await new Promise<void>((resolve, reject) => {
      const onAbort = () => {
        clearTimeout(timer);
        reject(signal.reason);
      };
      const timer = setTimeout(() => {
        signal.removeEventListener("abort", onAbort);
        resolve();
      }, requestTimestamps[0] + RATE_LIMIT_WINDOW_MS - now);
      signal.addEventListener("abort", onAbort, { once: true });
    });
  }
}

export const api = ky.create({
  prefixUrl: baseUrl,
  timeout: 500000, // TODO - set a reasonable timeout
  credentials: "omit",
  retry: {
    retryOnTimeout: true,
    limit: 10,
    statusCodes: [408, 413, 429, 500, 502, 503, 504, 401],
  },
  hooks: {
    beforeRequest: [
      async (request) => {
        const queryCache = queryClient.getQueryCache();
        const query = queryCache.get(hashKey(authQueryOptions.queryKey));
        if (query?.promise) {
          // Only awaited as a barrier, so a failed login shouldn't be rethrown here - it would
          // reject this request with the auth error instead of its own 401, and get put through
          // the retry ladder even though it can never resolve. The auth error is still held on
          // the query itself and surfaces through AuthProvider.
          await query.promise.catch(() => {});
        }

        await waitForRateLimit(request.signal);

        const token = storage.getToken();
        if (token) {
          request.headers.set("Authorization", `Bearer ${token}`);
        }
      },
    ],
    beforeRetry: [
      async ({ request, error }) => {
        if (error instanceof HTTPError) {
          if (error.response.status === 401) {
            const body: LassoException = await error.response.clone().json();
            if (!body.errorMessage?.includes("connection info")) {
              const authHeader = request.headers.get("Authorization");
              if (authHeader === `Bearer ${storage.getToken()}`) {
                await queryClient.invalidateQueries({
                  queryKey: ["auth"],
                  fetchStatus: "idle",
                });
              }
            }
          }
        }
      },
    ],
  },
});

export const queryClient = new QueryClient({
  defaultOptions: {},
});
